/**
 * Procurement health — Stage 5.
 * Sources `/api/bi/procurement/health/`.
 */
import { useMemo } from 'react';
import { ClipboardCheck, Package, RotateCcw, Sparkles, Truck } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { KPICard } from '@/components/charts/KPICard';
import { Sparkline } from '@/components/charts/Sparkline';
import { useProcurementHealth } from '@/api/bi';
import { rangeISO, useFilters } from '@/state/filters';
import { fmt } from '@/lib/format';

interface StatusBoxProps {
  title: string;
  rows: Record<string, number>;
}

function StatusBox({ title, rows }: StatusBoxProps) {
  const entries = Object.entries(rows);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardBody>
        {entries.length === 0 ? (
          <EmptyState title="Nothing in this window" />
        ) : (
          <ul className="space-y-1.5">
            {entries
              .sort((a, b) => b[1] - a[1])
              .map(([k, v]) => (
                <li key={k} className="flex items-center justify-between rounded-md border border-border/60 bg-surface px-2.5 py-1.5 text-xs">
                  <span className="capitalize text-muted-foreground">{k.replace(/_/g, ' ')}</span>
                  <span className="kpi-num font-medium">{fmt.number(v)}</span>
                </li>
              ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

export function ProcurementPage() {
  const range = useFilters((s) => s.range);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const { start: startISO, end: endISO } = useMemo(
    () => rangeISO(range, timeRangeMode),
    [range, timeRangeMode],
  );
  const query = useProcurementHealth({ start: startISO, end: endISO });

  const k = query.data?.kpis;
  const trendValue = (query.data?.grn_trend ?? []).map((b) => ({ value: b.value }));
  const trendUnits = (query.data?.grn_trend ?? []).map((b) => ({ value: b.units }));

  return (
    <>
      <PageHeader
        eyebrow="Supply"
        title="Procurement health"
        description="Pulse-check on RFQs, POs, GRNs and vendor returns. The pipeline at a glance."
        actions={
          <Badge tone="info" className="gap-1">
            <Sparkles className="h-3 w-3" /> Stage 5
          </Badge>
        }
      />

      {query.isError ? (
        <div className="mt-4">
          <ErrorState description="Couldn't load procurement health." onRetry={() => void query.refetch()} />
        </div>
      ) : null}

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard
          label="GRN value"
          value={fmt.npr(k?.grn_value_window ?? 0)}
          loading={query.isLoading}
          trend={trendValue}
          hint="Sum of received qty × price in window."
        />
        <KPICard
          label="Units received"
          value={fmt.number(k?.grn_units_window ?? 0)}
          loading={query.isLoading}
          trend={trendUnits}
        />
        <KPICard
          label="Fill rate"
          value={k?.fill_rate_window != null ? fmt.pct(k.fill_rate_window) : '—'}
          loading={query.isLoading}
          hint="Received / expected (window)."
        />
        <KPICard
          label="Vendor returns"
          value={fmt.npr(k?.vendor_returns_value ?? 0)}
          goodWhenUp={false}
          loading={query.isLoading}
          hint={`${fmt.number(k?.vendor_returns_units ?? 0)} units sent back`}
        />
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader>
            <CardTitle className="inline-flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4" /> RFQs
            </CardTitle>
          </CardHeader>
          <CardBody className="space-y-1 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">In window</span>
              <span className="kpi-num font-medium">{fmt.number(k?.rfq_in_window ?? 0)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Open now</span>
              <Badge tone={k && k.rfq_open_now > 0 ? 'warning' : 'success'}>
                {fmt.number(k?.rfq_open_now ?? 0)}
              </Badge>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="inline-flex items-center gap-2">
              <Package className="h-4 w-4" /> POs
            </CardTitle>
          </CardHeader>
          <CardBody className="space-y-1 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">In window</span>
              <span className="kpi-num font-medium">{fmt.number(k?.po_in_window ?? 0)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Open now</span>
              <Badge tone="info">{fmt.number(k?.po_open_now ?? 0)}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Late now</span>
              <Badge tone={k && k.po_late_now > 0 ? 'danger' : 'success'}>
                {fmt.number(k?.po_late_now ?? 0)}
              </Badge>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="inline-flex items-center gap-2">
              <Truck className="h-4 w-4" /> GRNs
            </CardTitle>
          </CardHeader>
          <CardBody className="space-y-1 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">In window</span>
              <span className="kpi-num font-medium">{fmt.number(k?.grn_in_window ?? 0)}</span>
            </div>
            {query.isLoading ? (
              <Skeleton className="h-12 w-full" />
            ) : (
              <Sparkline data={trendUnits} tone="primary" />
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="inline-flex items-center gap-2">
              <RotateCcw className="h-4 w-4" /> Vendor returns
            </CardTitle>
          </CardHeader>
          <CardBody className="space-y-1 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Returns</span>
              <span className="kpi-num font-medium">{fmt.number(k?.vendor_returns_window ?? 0)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Value</span>
              <span className="kpi-num font-medium">{fmt.npr(k?.vendor_returns_value ?? 0)}</span>
            </div>
          </CardBody>
        </Card>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <StatusBox title="RFQ status mix" rows={query.data?.rfq_by_status ?? {}} />
        <StatusBox title="PO status mix" rows={query.data?.po_by_status ?? {}} />
        <StatusBox title="GRN status mix" rows={query.data?.grn_by_status ?? {}} />
      </div>
    </>
  );
}
